const COLORS = {
	government: [98, 98, 98], // #6D6D6E
	free: [0, 153, 97], // #009961
	spouse: [209, 103, 107], // #d1676b
	firstParentela: [193, 131, 0], // #c18300
	secondParentela: [99, 126, 178], // #637eb2
	thirdParentela: [103, 147, 174] // #6793ae
};

/**
 * Colorize share
 * @param {String} key
 * @param {Object} [options]
 * @param {Number} [options.level] Nesting level, will reduce alpha
 * @param {Number} [options.index] Index within group, will currently reduce alpha, too
 * @param {Boolean} [options.hasParts] Whether there are sub parts
 * @return {String}
 */
function colorize(key, { level, index, hasParts } = {}) {
	let color = COLORS[key];

	if (!color) {
		// Custom shares
		const hue = 200 + (index || 0) * 20;

		color = `hsl(${hue} 60% 70%)`;
	} else {
		let alpha = 1;

		if (hasParts) {
			alpha = 0.2;
		} else if (level) {
			alpha = Math.max(0.2, 1 - level * 0.2 - index * 0.1);
		}

		color = `rgba(${color.join(', ')}, ${alpha})`;
	}

	return color;
}

/**
 * Sanitize value
 * @param {Number} [value]
 * @return {Number}
 */
function sanitizeValue(value = 0) {
	const sanitized = Number.isNaN(value) ? 0 : Math.max(value, 0);

	return sanitized;
}

/**
 * Round value to 0.XX
 * @param {Number} value
 * @param {Object} [options]
 * @param {Boolean} [options.rough] Whether to round to 0.25
 * @return {Number}
 */
function roundValue(value, { rough } = {}) {
	let rounded = Number(value.toFixed(2));

	if (rough) {
		rounded = Math.round((value + Number.EPSILON) * 4) / 4;
	}

	return rounded;
}

/**
 * Transform nested share
 * @param {Array.<Share>} shares
 * @param {Function} transformation
 * @param {Object} [options]
 * @param {Number} [options.level] Nesting level
 * @param {String} [options.groupKey]
 * @returns {Array.<Share>}
 */
function transform(shares, transformation, { level = 0, groupKey } = {}) {
	const transformedShares = shares.map((share, index) => {
		const transformedShare = transformation(
			{ ...share },
			{ level, index, groupKey: groupKey || share.key, total: shares.length }
		);

		if (transformedShare.parts) {
			transformedShare.parts = transform(transformedShare.parts, transformation, {
				level: level + 1,
				groupKey: groupKey || share.key
			});
		}

		return transformedShare;
	});

	return transformedShares;
}

/**
 * Filter nested share
 * @param {Array.<Share>} shares
 * @param {Function} check
 * @param {Object} [options]
 * @param {Number} [options.level] Nesting level
 * @param {Number} [options.index] Index within group
 * @param {String} [options.groupKey]
 * @returns {Array.<Share>}
 */
function filter(shares, check, { level = 0, groupKey } = {}) {
	const filteredShares = shares
		.map(share => {
			const transformedShare = { ...share };

			if (transformedShare.parts) {
				transformedShare.parts = filter(transformedShare.parts, check, {
					level: level + 1,
					groupKey: groupKey || share.key
				});
			}

			return transformedShare;
		})
		.filter((share, index) => {
			const checksOut = check(share, {
				level,
				index,
				groupKey: groupKey || share.key
			});

			return checksOut;
		});

	return filteredShares;
}

/**
 * Flatten nested tree into array of leaves
 * @param {Array.<Share>} shares
 * @return {Array.<Share>}
 */
function flatten(shares) {
	const flattenedShares = shares.reduce((acc, share) => {
		const flattenedShare = share.parts ? flatten(share.parts) : share;

		acc = acc.concat(flattenedShare);

		return acc;
	}, []);

	return flattenedShares;
}

/**
 * Deep clone
 * @param {Array|Object} input
 * @return {Array|Object}
 */
function deepClone(input) {
	const clone = JSON.parse(JSON.stringify(input));

	return clone;
}

/**
 * Capitalize string
 * @param {String} string
 * @return {String}
 */
function capitalize(string = '') {
	const capitalized = string.charAt(0).toUpperCase() + string.slice(1);

	return capitalized;
}

/**
 * Get title based on nesting level
 * @param {Object} options
 * @param {String} options.defaultTitle
 * @param {Object} options.i18n
 * @param {String} options.key
 * @param {String} options.groupKey
 * @param {Number} [options.level=0]
 * @param {Number} [options.index=0]
 * @param {Boolean} [options.isSingle]
 * @return {{ mainTitle: String, leveledTitle: String, namedTitle: String, combinedTitle: String }}
 */
function getTitle({
	defaultTitle,
	i18n,
	key,
	groupKey,
	level = 0,
	index = 0,
	isSingle = false
} = {}) {
	// Find title based on level, stop at level 5
	const maxLevel = Math.min(level, 5);
	const displayIndex = level && index ? index + 1 : 0;
	const leveledKey = `${groupKey}${maxLevel || ''}`;
	const leveledTitle = `${i18n[leveledKey] || ''} ${displayIndex || ''}`.trim();

	// Find title based on specific key
	const namedKey = `${leveledKey}${capitalize(key)}`;
	const namedTitle = i18n[namedKey];

	// Custom title if parents/grandparents are both deceased and combined into single item
	const combinedKey =
		isSingle &&
		((groupKey === 'secondParentela' && level === 1) ||
			(groupKey === 'thirdParentela' && level === 2))
			? `${leveledKey}Combined`
			: null;
	const combinedTitle = i18n[combinedKey];

	// Prioritize
	const mainTitle = combinedTitle || defaultTitle || namedTitle || leveledTitle;

	return {
		mainTitle,
		leveledTitle,
		namedTitle,
		combinedTitle
	};
}

/**
 * Add thousands separator
 * @param {Number} number
 * @return {String}
 */
function formatNumber(number = 0) {
	const formatted = number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'");

	return formatted;
}

export {
	sanitizeValue,
	roundValue,
	transform,
	filter,
	flatten,
	colorize,
	deepClone,
	getTitle,
	capitalize,
	formatNumber
};
